import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import LanguageSelector from './LanguageSelector';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_619b4d5e-f51b-467f-ae93-a95fb1e339d8/artifacts/b8ja0iao_BE-noBIZZ.png';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { t } = useLanguage();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  const navLinks = [
    { href: '#services', label: t('nav.services') },
    { href: '#how-it-works', label: t('nav.howItWorks') },
    { href: '#case-studies', label: t('nav.caseStudies') },
    { href: '#contact', label: t('nav.contact') }
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const isMainPage = typeof window !== 'undefined' && window.location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="BIZZ Europe" className="h-14 w-auto" />
            <span
              className="text-[#1a237e]"
              style={{ fontFamily: "'Rubik', sans-serif", fontSize: 'calc(3.5rem * 0.35)', fontWeight: 700, letterSpacing: '0.01em' }}
            >
              BIZZ Europe
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {isMainPage && navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-slate-700 hover:text-[#1a237e] transition-colors duration-200"
              >
                {link.label}
              </a>
            ))}

            <LanguageSelector />

            {/* Auth Button */}
            {!loading && (
              user ? (
                <div ref={userMenuRef} className="relative">
                  <button
                    data-testid="user-menu-button"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#1a237e] flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{(user.name || user.email || '?')[0].toUpperCase()}</span>
                    </div>
                    <span className="max-w-[100px] truncate">{user.name || user.email}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isUserMenuOpen && (
                    <div data-testid="user-dropdown-menu" className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                      <Link
                        data-testid="dashboard-link"
                        to="/dashboard"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        {t('dashboard.title')}
                      </Link>
                      <button
                        data-testid="logout-button"
                        onClick={handleLogout}
                        className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        {t('auth.logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  data-testid="navbar-login-button"
                  to="/login"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1a237e] text-white text-sm font-medium hover:bg-[#0d1442] transition-colors"
                >
                  <User className="w-4 h-4" />
                  {t('auth.login')}
                </Link>
              )
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100 bg-white">
            <div className="flex flex-col gap-4">
              {isMainPage && navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-slate-700 hover:text-[#1a237e] py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-2">
                <LanguageSelector />
              </div>
              {!loading && (
                <div className="pt-2 border-t border-slate-100">
                  {user ? (
                    <>
                      <Link
                        to="/dashboard"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#1a237e] py-2"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        {t('dashboard.title')}
                      </Link>
                      <button
                        onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                        className="flex items-center gap-2 text-sm font-medium text-red-600 py-2"
                      >
                        <LogOut className="w-4 h-4" />
                        {t('auth.logout')}
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 text-sm font-medium text-[#1a237e] py-2"
                    >
                      <User className="w-4 h-4" />
                      {t('auth.login')}
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
